import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Pagination,
  Stack,
  Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TimelineIcon from '@mui/icons-material/Timeline';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import { BaseTableList } from 'components/AgGrid/AgGrid';
import { useAppSelector } from 'store/hook';
import { selectTireInventory } from 'store/tire-inventory.slice';
import TireInventoryHandler from 'handlers/tire-inventory.handler';
import useAppTheme from 'hooks/useAppTheme';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { type ColDef } from 'ag-grid-community';
import { type TireInventory } from 'types/tire-inventory.types';
import { type Asset } from 'types/asset.types';
import { ThemePalette } from 'mui.theme';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { GridSearchIcon } from '@mui/x-data-grid';
import { getDynamicAssetTireInfo } from 'views/TirePressure/util';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

// ─── Mode Types ───────────────────────────────────────────────────────────────
type SelectionMode = 'serial' | 'asset';

// ─── Sub-components ──────────────────────────────────────────────────────────
const DateRenderer = (props: any) => {
  if (!props.value) return <span>--</span>;
  return <span>{convertStdDateString(props.value, 'MMM DD, YYYY')}</span>;
};

// ─── Main Component ──────────────────────────────────────────────────────────
const Timeline = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const navigate = useNavigate();

  // Redux state
  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  const { tireInventory, isLoading, total, totalPages } =
    useAppSelector(selectTireInventory);
  const assets = useAppSelector((state) => state.assetReducer.assets);
  const tirePatternPositions = useAppSelector(
    (state) => state.tirePatternReducer.tirePatternPositions
  );
  const makeModelDetails = useAppSelector(
    (state) => state.makeModelDetailsReducer
  );

  // Mode state
  const [mode, setMode] = useState<SelectionMode>('serial');

  // ── Serial Number mode state ───────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');

  // ── Asset & Position mode state ────────────────────────────────────────────
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedPosition, setSelectedPosition] = useState('');

  const tireInventoryHandler = new TireInventoryHandler();

  // ── TPMS-enabled assets ────────────────────────────────────────────────────
  const tpmsAssets = useMemo(
    () => assets.filter((a) => a.tpmsEnabled),
    [assets]
  );

  // ── Dynamic tire positions for selected asset ──────────────────────────────
  const availablePositions = useMemo(() => {
    if (
      !selectedAsset ||
      !tirePatternPositions.length ||
      !makeModelDetails.length
    ) {
      return [];
    }
    const { tireCodes } = getDynamicAssetTireInfo(
      makeModelDetails,
      tirePatternPositions,
      selectedAsset.make,
      selectedAsset.model,
      customerId
    );
    return Object.entries(tireCodes).map(([code, label]) => ({
      code,
      label,
    }));
  }, [selectedAsset, tirePatternPositions, makeModelDetails, customerId]);

  // ── Debounce search keyword ────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(searchKeyword);
      setCurrentPage(1);
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [searchKeyword]);

  // ── Fetch tire inventory (serial number mode) ──────────────────────────────
  useEffect(() => {
    if (mode !== 'serial') return;
    void tireInventoryHandler.getTireInventory({
      customerId,
      page: currentPage,
      limit: pageSize,
      keyword: debouncedKeyword || undefined,
    });
  }, [customerId, currentPage, pageSize, debouncedKeyword, mode]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: SelectionMode | null
  ) => {
    if (newMode) setMode(newMode);
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(event.target.value as number);
    setCurrentPage(1);
  };

  const handleAssetChange = (_: any, asset: Asset | null) => {
    setSelectedAsset(asset);
    setSelectedPosition('');
  };

  const handleGenerateTimeline = () => {
    if (selectedAsset && selectedPosition) {
      navigate(
        `/tire-management/timeline/detail?assetId=${encodeURIComponent(
          selectedAsset.assetId
        )}&position=${encodeURIComponent(selectedPosition)}`
      );
    }
  };

  // ── AG Grid column definitions ─────────────────────────────────────────────
  const columnDefs: Array<ColDef<TireInventory>> = [
    {
      field: 'tireSerialNumber',
      headerName: 'Serial Number',
      minWidth: 220,
      flex: 1,
      cellStyle: {
        fontWeight: 600,
        color: enableDarkTheme
          ? ThemePalette.typography.white
          : ThemePalette.typography.primary,
      },
    },
    {
      field: 'tireManufacturer',
      headerName: t('manufacturer'),
      minWidth: 140,
      flex: 1,
    },
    {
      field: 'tireModel',
      headerName: t('model_10'),
      minWidth: 120,
      flex: 1,
    },
    {
      field: 'tireSize',
      headerName: t('size'),
      minWidth: 120,
      flex: 1,
    },
    {
      field: 'createdAt',
      headerName: t('onboarding_date'),
      minWidth: 160,
      flex: 1,
      cellRenderer: DateRenderer,
    },
    {
      headerName: 'Action',
      minWidth: 180,
      maxWidth: 200,
      pinned: 'right' as const,
      sortable: false,
      filter: false,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      cellRenderer: (props: any) => (
        <Button
          size="small"
          variant="contained"
          startIcon={<TimelineIcon sx={{ fontSize: 14 }} />}
          onClick={() => {
            navigate(
              `/tire-management/timeline/detail?serialNumber=${encodeURIComponent(
                props.data.tireSerialNumber
              )}`
            );
          }}
          sx={{
            backgroundColor: ThemePalette.typography.primary,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.75rem',
            px: 1.5,
            py: 0.5,
            '&:hover': {
              backgroundColor: ThemePalette.typography.primary,
              opacity: 0.9,
            },
          }}
        >
          {t('generate_timeline')}
        </Button>
      ),
    },
  ];

  // ── Shared palette helpers ─────────────────────────────────────────────────
  const bg = enableDarkTheme ? palette.pageBackground : palette.pageBackground;
  const cardBg = enableDarkTheme
    ? palette.boxBackground
    : palette.boxBackground;
  const borderColor = enableDarkTheme
    ? 'rgba(255,255,255,0.12)'
    : 'rgba(0,0,0,0.1)';
  const textSecondary = enableDarkTheme
    ? 'rgba(255,255,255,0.55)'
    : 'rgba(0,0,0,0.55)';

  return (
    <CustomMuiThemeProvider>
      <Box sx={{ p: 3, backgroundColor: bg, minHeight: '100%' }}>
        {/* ── Page Header ── */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}
          >
            <TimelineIcon
              sx={{
                color: ThemePalette.typography.primary,
                fontSize: 28,
              }}
            />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: palette.textPrimary,
              }}
            >
              {t('tire_timeline')}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: textSecondary, ml: 5.5 }}>
            {t('view_tire_history_by_serial_number_or_by_asset_and_position')}
          </Typography>
        </Box>

        {/* ── Mode Toggle ── */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            sx={{
              backgroundColor: cardBg,
              border: `1px solid ${borderColor}`,
              borderRadius: '10px',
              overflow: 'hidden',
              '& .MuiToggleButton-root': {
                border: 'none',
                px: 3,
                py: 1.2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: textSecondary,
                gap: 1,
                '&.Mui-selected': {
                  backgroundColor: ThemePalette.typography.primary,
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: ThemePalette.typography.primary,
                    opacity: 0.9,
                  },
                },
                '&:hover': {
                  backgroundColor: enableDarkTheme
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(0,0,0,0.04)',
                },
              },
            }}
          >
            <ToggleButton value="serial">
              <QrCodeIcon fontSize="small" />
              {t('by_serial_number')}
            </ToggleButton>
            <ToggleButton value="asset">
              <DirectionsCarIcon fontSize="small" />
              {t('by_asset_amp_position')}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* MODE: Serial Number                                               */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {mode === 'serial' && (
          <Box>
            {/* Controls Row */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ color: textSecondary }}>
                  {isLoading ? (
                    'Loading...'
                  ) : (
                    <>
                      Showing {tireInventory.length} of {total} tires
                      {totalPages > 1 &&
                        ` • Page ${currentPage} of ${totalPages}`}
                      {debouncedKeyword &&
                        ` • Filtered by: "${debouncedKeyword}"`}
                    </>
                  )}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {/* Search */}
                <TextField
                  size="small"
                  placeholder={t('search_tires')}
                  value={searchKeyword}
                  onChange={(e) => {
                    setSearchKeyword(e.target.value);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <GridSearchIcon sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: '240px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(0,0,0,0.02)',
                    },
                  }}
                />

                {/* Page Size */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>{t('rows_per_page')}</InputLabel>
                  <Select
                    value={pageSize}
                    label={t('rows_per_page')}
                    onChange={handlePageSizeChange}
                    sx={{
                      color: palette.textPrimary,
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor,
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Table */}
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '420px',
                  backgroundColor: cardBg,
                  borderRadius: '8px',
                }}
              >
                <CircularProgress size={40} />
              </Box>
            ) : tireInventory.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '420px',
                  backgroundColor: cardBg,
                  borderRadius: '8px',
                  border: `1px dashed ${borderColor}`,
                  gap: 1,
                }}
              >
                <TireRepairIcon
                  sx={{ fontSize: 48, color: textSecondary, opacity: 0.5 }}
                />
                <Typography variant="h6" sx={{ color: textSecondary }}>
                  {t('no_tires_found')}
                </Typography>
                {debouncedKeyword && (
                  <Typography variant="body2" sx={{ color: textSecondary }}>
                    {t('no_results_for_quot')}
                    {debouncedKeyword}
                    {t('quot')}
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                <BaseTableList
                  rowData={tireInventory}
                  columnDefs={columnDefs}
                  pagination={false}
                  tableStyles={{
                    height: '50vh',
                    borderRadius: '8px',
                  }}
                  getRowId={(params: any) => params.data.tireSerialNumber}
                />

                {/* Pagination */}
                {totalPages > 1 && (
                  <Stack
                    spacing={2}
                    sx={{
                      mt: 2.5,
                      alignItems: 'center',
                      pb: 2,
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                      sx={{
                        '& .MuiPaginationItem-root': {
                          color: palette.textPrimary,
                          borderColor,
                        },
                        '& .Mui-selected': {
                          backgroundColor: `${ThemePalette.typography.primary} !important`,
                          color: 'white !important',
                        },
                      }}
                    />
                  </Stack>
                )}
              </>
            )}
          </Box>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* MODE: Asset & Position                                            */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {mode === 'asset' && (
          <Box>
            {/* Selector Card */}
            <Box
              sx={{
                backgroundColor: cardBg,
                borderRadius: '12px',
                border: `1px solid ${borderColor}`,
                p: 3,
                mb: 3,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: palette.textPrimary,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <DirectionsCarIcon
                  fontSize="small"
                  sx={{ color: ThemePalette.typography.primary }}
                />
                {t('select_asset_amp_tire_position')}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                }}
              >
                {/* Asset Autocomplete */}
                <Autocomplete
                  options={tpmsAssets}
                  value={selectedAsset}
                  onChange={handleAssetChange}
                  getOptionLabel={(option) =>
                    `${option.bumperNumber} — ${option.make} ${option.model}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.assetId === value.assetId
                  }
                  noOptionsText={
                    tpmsAssets.length === 0
                      ? 'No TPMS-enabled assets found'
                      : 'No matching asset'
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('tpms_enabled_asset')}
                      placeholder={t('search_asset_3')}
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon
                                sx={{ fontSize: 18, color: textSecondary }}
                              />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: enableDarkTheme
                            ? 'rgba(255,255,255,0.05)'
                            : 'rgba(0,0,0,0.02)',
                        },
                      }}
                    />
                  )}
                  sx={{ minWidth: 300, flex: 1 }}
                />

                {/* Tire Position */}
                <FormControl
                  size="small"
                  disabled={!selectedAsset || availablePositions.length === 0}
                  sx={{ minWidth: 200, flex: 1 }}
                >
                  <InputLabel>{t('tire_position')}</InputLabel>
                  <Select
                    value={selectedPosition}
                    label={t('tire_position')}
                    onChange={(e) => {
                      setSelectedPosition(e.target.value);
                    }}
                    sx={{
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255,255,255,0.05)'
                        : 'rgba(0,0,0,0.02)',
                      color: palette.textPrimary,
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor,
                      },
                    }}
                  >
                    {availablePositions.length === 0 ? (
                      <MenuItem value="" disabled>
                        {selectedAsset
                          ? 'No positions available'
                          : 'Select an asset first'}
                      </MenuItem>
                    ) : (
                      availablePositions.map((pos) => (
                        <MenuItem key={pos.code} value={pos.label}>
                          {pos.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                {/* Generate Button */}
                <Button
                  variant="contained"
                  startIcon={<TimelineIcon />}
                  disabled={!selectedAsset || !selectedPosition}
                  onClick={handleGenerateTimeline}
                  sx={{
                    backgroundColor: ThemePalette.typography.primary,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    whiteSpace: 'nowrap',
                    alignSelf: 'flex-start',
                    '&:hover': {
                      backgroundColor: ThemePalette.typography.primary,
                      opacity: 0.9,
                    },
                    '&:disabled': {
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255,255,255,0.12)'
                        : 'rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  {t('generate_timeline')}
                </Button>
              </Box>

              {/* Selection Summary */}
              {selectedAsset && (
                <Box
                  sx={{
                    mt: 2.5,
                    pt: 2,
                    borderTop: `1px solid ${borderColor}`,
                    display: 'flex',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="caption" sx={{ color: textSecondary }}>
                    {t('selected')}
                  </Typography>
                  <Chip
                    label={`Asset: ${selectedAsset.bumperNumber}`}
                    size="small"
                    sx={{
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.07)',
                      color: palette.textPrimary,
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={`Make/Model: ${selectedAsset.make} ${selectedAsset.model}`}
                    size="small"
                    sx={{
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.07)',
                      color: palette.textPrimary,
                    }}
                  />
                  {selectedPosition && (
                    <Chip
                      label={`Position: ${selectedPosition}`}
                      size="small"
                      color="success"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
              )}
            </Box>

            {/* Empty State for Asset Mode */}
            {!selectedAsset && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '35vh',
                  backgroundColor: cardBg,
                  borderRadius: '12px',
                  border: `1px dashed ${borderColor}`,
                  gap: 1.5,
                }}
              >
                <DirectionsCarIcon
                  sx={{ fontSize: 56, color: textSecondary, opacity: 0.4 }}
                />
                <Typography
                  variant="h6"
                  sx={{ color: textSecondary, fontWeight: 600 }}
                >
                  {t('select_an_asset_to_begin')}
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary }}>
                  {t('only_tpms_enabled_assets_are_listed')}
                  {tpmsAssets.length} {t('available')}
                </Typography>
              </Box>
            )}

            {selectedAsset && !selectedPosition && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '35vh',
                  backgroundColor: cardBg,
                  borderRadius: '12px',
                  border: `1px dashed ${borderColor}`,
                  gap: 1.5,
                }}
              >
                <TireRepairIcon
                  sx={{ fontSize: 56, color: textSecondary, opacity: 0.4 }}
                />
                <Typography
                  variant="h6"
                  sx={{ color: textSecondary, fontWeight: 600 }}
                >
                  {t('now_select_a_tire_position')}
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary }}>
                  {availablePositions.length} position
                  {availablePositions.length !== 1 ? 's' : ''}{' '}
                  {t('available_for')} {selectedAsset.bumperNumber}
                </Typography>
              </Box>
            )}

            {selectedAsset && selectedPosition && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '35vh',
                  backgroundColor: cardBg,
                  borderRadius: '12px',
                  border: `1px dashed ${ThemePalette.typography.primary}`,
                  gap: 1.5,
                }}
              >
                <TimelineIcon
                  sx={{
                    fontSize: 56,
                    color: ThemePalette.typography.primary,
                    opacity: 0.7,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: palette.textPrimary,
                    fontWeight: 600,
                  }}
                >
                  {t('ready_to_generate_timeline')}
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary }}>
                  {selectedAsset.bumperNumber} — {selectedPosition}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<TimelineIcon />}
                  onClick={handleGenerateTimeline}
                  sx={{
                    mt: 1,
                    backgroundColor: ThemePalette.typography.primary,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    '&:hover': {
                      backgroundColor: ThemePalette.typography.primary,
                      opacity: 0.9,
                    },
                  }}
                >
                  {t('generate_timeline')}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </CustomMuiThemeProvider>
  );
};

export default Timeline;
